import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Flip as FlipIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import type { PublicSitePlanPayload } from '../../../services/superPickerPublicApi';
import type { PublicUnitForMenu } from '../unit/types';
import FullVersionTooltip, { GATED_DISABLED_SX } from '../FullVersionTooltip';
import type { CompareQuickRefColumn } from './FloorPlanComparisonViewer';
import { getCompareSitePlanMarker } from './sitePlanCompareUtils';
import {
  focusCompareSitePlanMarker,
  getCompareSitePlanMarkerElementId,
  type CompareSitePlanZoomToElement,
} from './compareSitePlanAutoFocus';
import { useIsMobile } from '../../../hooks/mobile-hooks';

export interface PublicSitePlanCompareColumn {
  unit: PublicUnitForMenu;
  sitePlan: PublicSitePlanPayload | null;
  loading: boolean;
}

interface SitePlanComparisonViewerProps {
  columns: PublicSitePlanCompareColumn[];
  quickRefColumns: CompareQuickRefColumn[];
  showPoints: boolean;
}

/** Agent compare-site-plan dot styling (`SuperPickerApp.tsx` ~line 27180). */
const COMPARE_SITE_PLAN_DOT_SIZE_DESKTOP = 28;
const COMPARE_SITE_PLAN_DOT_SIZE_MOBILE = 14;
const COMPARE_SITE_PLAN_DOT_BORDER_DESKTOP = '2px solid var(--color-white)';
const COMPARE_SITE_PLAN_DOT_BORDER_MOBILE = '1px solid var(--color-white)';
const COMPARE_SITE_PLAN_DOT_SHADOW_DESKTOP = '0 2px 4px rgba(var(--color-shadow-rgb), 0.3)';
const COMPARE_SITE_PLAN_DOT_SHADOW_MOBILE = '0 1px 2px rgba(var(--color-shadow-rgb), 0.25)';

const GatedToolbarButton: React.FC<{
  label: string;
  icon: React.ReactNode;
}> = ({ label, icon }) => (
  <FullVersionTooltip>
    <span>
      <IconButton size="small" disabled aria-label={label} sx={GATED_DISABLED_SX}>
        {icon}
      </IconButton>
    </span>
  </FullVersionTooltip>
);

/** Auto-zoom to the unit's stack marker when the visualizer mounts or updates. */
const CompareSitePlanAutoFocus: React.FC<{
  enabled: boolean;
  markerElementId: string;
  focusKey: string;
  zoomToElement: CompareSitePlanZoomToElement;
}> = ({ enabled, markerElementId, focusKey, zoomToElement }) => {
  const zoomToElementRef = useRef(zoomToElement);

  useEffect(() => {
    zoomToElementRef.current = zoomToElement;
  }, [zoomToElement]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let firstFrame = 0;
    let secondFrame = 0;

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (!cancelled) {
          focusCompareSitePlanMarker(markerElementId, zoomToElementRef.current);
        }
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [enabled, markerElementId, focusKey]);

  return null;
};

export const SitePlanComparisonViewer: React.FC<SitePlanComparisonViewerProps> = ({
  columns,
  quickRefColumns,
  showPoints,
}) => {
  const isMobileSitePlan = useIsMobile();
  const sitePlanDotSize = isMobileSitePlan
    ? COMPARE_SITE_PLAN_DOT_SIZE_MOBILE
    : COMPARE_SITE_PLAN_DOT_SIZE_DESKTOP;
  const sitePlanDotBorder = isMobileSitePlan
    ? COMPARE_SITE_PLAN_DOT_BORDER_MOBILE
    : COMPARE_SITE_PLAN_DOT_BORDER_DESKTOP;
  const sitePlanDotShadow = isMobileSitePlan
    ? COMPARE_SITE_PLAN_DOT_SHADOW_MOBILE
    : COMPARE_SITE_PLAN_DOT_SHADOW_DESKTOP;

  const [quickRefOpen, setQuickRefOpen] = useState(true);
  // Side panel on tablet landscape + desktop; bottom bar on phone and tablet portrait.
  const quickRefSideBySide = useMediaQuery('(min-width:600px) and (orientation: landscape), (min-width:1200px)');
  const [focusedUnitKey, setFocusedUnitKey] = useState<string | null>(
    columns[0]?.unit.unitKey ?? null,
  );

  if (columns.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
          No units selected for site plan comparison.
        </Typography>
      </Box>
    );
  }

  const tripleColumnFit = columns.length === 3;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: quickRefSideBySide
          ? (quickRefOpen ? 'minmax(0, 1fr) 200px' : 'minmax(0, 1fr) 36px')
          : '1fr',
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Box sx={{ overflowX: tripleColumnFit ? 'hidden' : 'auto', minWidth: 0, width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            width: '100%',
            minWidth: tripleColumnFit ? 0 : Math.max(820, columns.length * 420),
            alignItems: 'stretch',
          }}
        >
          {columns.slice(0, 3).map((col) => {
            const isFocused = col.unit.unitKey === focusedUnitKey;
            const marker = getCompareSitePlanMarker(col.sitePlan, col.unit);
            const imageUrl = col.sitePlan?.imageUrl;
            const markerElementId = marker
              ? getCompareSitePlanMarkerElementId(col.unit.unitKey)
              : null;
            const markerFocusKey = marker
              ? [
                  markerElementId,
                  imageUrl,
                  marker.left,
                  marker.top,
                  marker.width,
                  marker.height,
                ].join(':')
              : '';

            return (
              <Box
                key={col.unit.unitKey}
                sx={{
                  flex: '1 1 0',
                  minWidth: tripleColumnFit ? 0 : 380,
                  maxWidth: tripleColumnFit ? '100%' : undefined,
                  border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                  boxShadow: isFocused ? '0 0 0 2px rgba(var(--color-accent-rgb), 0.6)' : 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1,
                    py: 0.5,
                    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
                    gap: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                    {col.unit.projectName} {col.unit.unitNumber}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 0 }}>
                    <GatedToolbarButton label="Mirror flip" icon={<FlipIcon fontSize="small" />} />
                    <GatedToolbarButton label="Rotate left" icon={<RotateLeftIcon fontSize="small" />} />
                    <GatedToolbarButton label="Rotate right" icon={<RotateRightIcon fontSize="small" />} />
                    <GatedToolbarButton label="Move left" icon={<ArrowBackIcon fontSize="small" />} />
                    <GatedToolbarButton label="Move right" icon={<ArrowForwardIcon fontSize="small" />} />
                  </Box>
                </Box>
                <Box
                  onClick={() => setFocusedUnitKey(col.unit.unitKey)}
                  sx={{
                    height: { xs: '50vh', md: '58vh' },
                    minHeight: 360,
                    p: 0.5,
                    cursor: 'grab',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {col.loading ? (
                    <CircularProgress size={26} />
                  ) : !imageUrl ? (
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(var(--color-text-rgb), 0.75)', textAlign: 'center', px: 2 }}
                    >
                      No site plan available for this project.
                    </Typography>
                  ) : (
                    <TransformWrapper
                      initialScale={1}
                      minScale={0.8}
                      maxScale={8}
                      centerOnInit
                      limitToBounds={false}
                      wheel={{ step: 0.08 }}
                      pinch={{ step: 5 }}
                      doubleClick={{ step: 0.8 }}
                    >
                      {({ zoomToElement }) => (
                        <>
                          {markerElementId && (
                            <CompareSitePlanAutoFocus
                              enabled={showPoints && Boolean(marker)}
                              markerElementId={markerElementId}
                              focusKey={markerFocusKey}
                              zoomToElement={zoomToElement}
                            />
                          )}
                          <TransformComponent
                            wrapperStyle={{ width: '100%', height: '100%' }}
                            contentStyle={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                              <Box
                                component="img"
                                src={imageUrl}
                                alt={`Site plan for ${col.unit.projectName}`}
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                  display: 'block',
                                }}
                              />
                              {showPoints && marker && (
                                <Box
                                  id={markerElementId ?? undefined}
                                  sx={{
                                    position: 'absolute',
                                    left: `${marker.left}%`,
                                    top: `${marker.top}%`,
                                    width: `${marker.width}%`,
                                    height: `${marker.height}%`,
                                    pointerEvents: 'none',
                                    '@keyframes compareSitePlanBlink': {
                                      '0%, 100%': {
                                        opacity: 1,
                                        transform: 'translate(-50%, -50%) scale(1)',
                                      },
                                      '50%': {
                                        opacity: 0.3,
                                        transform: 'translate(-50%, -50%) scale(1.3)',
                                      },
                                    },
                                    '&::before': {
                                      content: `"${marker.label}"`,
                                      position: 'absolute',
                                      left: '50%',
                                      top: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      width: `${sitePlanDotSize}px`,
                                      height: `${sitePlanDotSize}px`,
                                      borderRadius: '50%',
                                      backgroundColor: 'var(--color-success)',
                                      border: sitePlanDotBorder,
                                      boxShadow: sitePlanDotShadow,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: isMobileSitePlan ? '0.65rem' : '0.75rem',
                                      fontWeight: 'bold',
                                      color: 'var(--color-white)',
                                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                                      animation: 'compareSitePlanBlink 0.5s ease-in-out infinite',
                                    },
                                  }}
                                />
                              )}
                              {showPoints && !marker && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    position: 'absolute',
                                    left: 8,
                                    bottom: 8,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 0.75,
                                    bgcolor: 'rgba(var(--color-shadow-rgb), 0.65)',
                                    color: 'var(--color-white)',
                                  }}
                                >
                                  Stack marker not found
                                </Typography>
                              )}
                            </Box>
                          </TransformComponent>
                        </>
                      )}
                    </TransformWrapper>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
          borderRadius: 1,
          p: quickRefOpen ? 1.25 : 0.5,
          maxHeight: quickRefSideBySide ? '74vh' : 'none',
          overflowY: 'auto',
          bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
          overflowX: quickRefSideBySide ? 'hidden' : 'auto',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: quickRefOpen ? 'space-between' : 'center',
            alignItems: 'center',
            mb: quickRefOpen ? 1 : 0,
          }}
        >
          {quickRefOpen && (
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Quick Reference
            </Typography>
          )}
          <Tooltip title={quickRefOpen ? 'Hide Quick Reference' : 'Show Quick Reference'}>
            <IconButton size="small" onClick={() => setQuickRefOpen((prev) => !prev)}>
              {quickRefOpen ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        {quickRefOpen && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: quickRefSideBySide ? 'column' : 'row',
              gap: 1,
              alignItems: 'stretch',
              overflowX: quickRefSideBySide ? 'visible' : 'auto',
            }}
          >
            {quickRefColumns.map((col) => (
              <Box
                key={`site-ref-${col.unit.unitKey}`}
                onClick={() => setFocusedUnitKey(col.unit.unitKey)}
                sx={{
                  flex: quickRefSideBySide ? 'none' : '0 0 auto',
                  width: quickRefSideBySide ? 'auto' : 148,
                  p: 0.85,
                  borderRadius: 1,
                  border: '1px solid rgba(var(--color-text-rgb), 0.14)',
                  cursor: 'pointer',
                  bgcolor:
                    col.unit.unitKey === focusedUnitKey
                      ? 'rgba(var(--color-accent-rgb), 0.14)'
                      : 'transparent',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  {col.unit.unitNumber}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Price: {col.priceDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  PSF: {col.psfDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Area: {col.areaDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Bedroom: {col.bedroomDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Bathroom: {col.bathroomDisplay}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SitePlanComparisonViewer;
